import { useState, useCallback } from "react";

/**
 * Custom hook for managing modal states
 * Implements Single Responsibility Principle by focusing only on modal state management
 */
export const useStaffModalManager = () => {
  const [modals, setModals] = useState({
    addEmployee: false,
    bulkImport: false,
    addRole: false,
    importRoles: false,
    addPermission: false,
    importPermissions: false,
    assignUsers: false,
    addNewUsers: false, // New modal for adding users to role
  });

  const [modalData, setModalData] = useState({
    selectedEmployee: null,
    selectedOriginalEmployee: null,
    modalMode: "create", // 'create', 'edit', 'view'
    selectedRole: null, // For role view/edit
    roleModalMode: "create", // 'create', 'edit', 'view' for roles
    permissionModalMode: "create", // 'create', 'edit', 'view' for permission
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generic modal open handler
  // Generic modal open handler
  const openModal = useCallback((modalName, data = {}) => {
    setModals((prev) => ({
      ...prev,
      [modalName]: true,
    }));

    if (data.hasOwnProperty("selectedEmployee")) {
      setModalData((prev) => ({
        ...prev,
        selectedEmployee: data.selectedEmployee,
        selectedOriginalEmployee: data.selectedOriginalEmployee,
        modalMode: data.mode || "create",
      }));
    }

    // Add role-specific data handling - check if property exists, not if truthy
    if (data.hasOwnProperty("selectedRole")) {
      setModalData((prev) => ({
        ...prev,
        selectedRole: data.selectedRole,
        roleModalMode: data.mode || "create",
      }));
    }
   if (data.hasOwnProperty("selectedPermission")) {
  setModalData((prev) => ({
    ...prev,
    selectedPermission: data.selectedPermission, // Fix: was setting selectedRole
    permissionModalMode: data.mode || "create",
  }));
}
  }, []);
  // Generic modal close handler
  // Generic modal close handler
const closeModal = useCallback((modalName) => {
  setModals((prev) => ({
    ...prev,
    [modalName]: false,
  }));

  // Only reset modal data for main modals, NOT nested ones
  if (!["addNewUsers"].includes(modalName)) {
    setModalData({
      selectedEmployee: null,
      selectedOriginalEmployee: null,
      modalMode: "create",
      selectedRole: null,
      roleModalMode: "create",
    });
  }

  setIsSubmitting(false);
}, []);
  // Specific modal handlers
  const handleAddEmployee = useCallback(() => {
    openModal("addEmployee", { mode: "create" });
  }, [openModal]);

  const handleEditEmployee = useCallback(
    (employee, originalEmployee) => {
      openModal("addEmployee", {
        mode: "edit",
        selectedEmployee: employee,
        selectedOriginalEmployee: originalEmployee,
      });
    },
    [openModal]
  );

  const handleViewEmployee = useCallback(
    (employee, originalEmployee) => {
      openModal("addEmployee", {
        mode: "view",
        selectedEmployee: employee,
        selectedOriginalEmployee: originalEmployee,
      });
    },
    [openModal]
  );

  const handleBulkImport = useCallback(() => {
    openModal("bulkImport");
  }, [openModal]);

  // Role handlers
  const handleAddRole = useCallback(() => {
    openModal("addRole", {
      mode: "create",
      selectedRole: null, // Explicitly pass null to clear previous role data
    });
  }, [openModal]);

  const handleEditRole = useCallback(
    (role) => {
      openModal("addRole", {
        mode: "edit",
        selectedRole: role,
      });
    },
    [openModal]
  );

  const handleViewRole = useCallback(
    (role) => {
      openModal("addRole", {
        mode: "view",
        selectedRole: role,
      });
    },
    [openModal]
  );

  const handleViewAssignedUsers = useCallback(
    (role) => {
      openModal("assignUsers", {
        mode: "view",
        selectedRole: role,
      });
    },
    [openModal]
  );

  const handleImportRoles = useCallback(() => {
    openModal("importRoles");
  }, [openModal]);

  const handleAddPermission = useCallback(() => {
    openModal("addPermission");
  }, [openModal]);
  const handleViewPermission = useCallback((permission) => {
  openModal("addPermission", {
    mode: "view",
    selectedPermission: permission,
  });
}, [openModal]);

const handleEditPermission = useCallback((permission) => {
  openModal("addPermission", {
    mode: "edit",
    selectedPermission: permission,
  });
}, [openModal]);

  const handleImportPermissions = useCallback(() => {
    openModal("importPermissions");
  }, [openModal]);

  // New handler for opening Add New Users modal
  const handleOpenAddNewUsers = useCallback(() => {
    openModal("addNewUsers");
  }, [openModal]);

  const handleCloseAddEmployeeModal = useCallback(() => {
    closeModal("addEmployee");
  }, [closeModal]);

  const handleCloseBulkImportModal = useCallback(() => {
    closeModal("bulkImport");
  }, [closeModal]);

  const handleCloseAddRoleModal = useCallback(() => {
    closeModal("addRole");
  }, [closeModal]);

  const handleCloseImportRolesModal = useCallback(() => {
    closeModal("importRoles");
  }, [closeModal]);

  const handleCloseAddPermissionModal = useCallback(() => {
    closeModal("addPermission");
  }, [closeModal]);

  const handleCloseImportPermissionsModal = useCallback(() => {
    closeModal("importPermissions");
  }, [closeModal]);

  const handleCloseAssignUsersModal = useCallback(() => {
    closeModal("assignUsers");
  }, [closeModal]);

  const handleCloseAddNewUsersModal = useCallback(() => {
    closeModal("addNewUsers");
  }, [closeModal]);

  return {
    // Modal states
    modals,
    modalData,
    isSubmitting,
    setIsSubmitting,

    // Generic handlers
    openModal,
    closeModal,

    // Specific handlers
    handleAddEmployee,
    handleEditEmployee,
    handleViewEmployee,
    handleBulkImport,
    handleAddRole,
    handleEditRole,
    handleViewRole,
    handleViewAssignedUsers,
    handleImportRoles,
    handleAddPermission,
    handleViewPermission,
    handleEditPermission,
    handleImportPermissions,
    handleOpenAddNewUsers,
    handleCloseAddEmployeeModal,
    handleCloseBulkImportModal,
    handleCloseAddRoleModal,
    handleCloseImportRolesModal,
    handleCloseAddPermissionModal,
    handleCloseImportPermissionsModal,
    handleCloseAssignUsersModal,
    handleCloseAddNewUsersModal,
  };
};
